/**
 * @file Renders the description list of an investment's fields, with an optional edit button.
 */

import React from "react";
import { Descriptions } from "antd";
import type { ReactNode } from "react";
import type { DescriptionsProps } from "antd";

import { EditButton } from "@components/Button";

import renderInvestmentItems from "./helpers/renderInvestmentItems";
import type { Tier, DescriptionItem, Investment as InvestmentItem } from "./helpers/types";

const { Item } = Descriptions;

const COLUMNS_COUNT = 1;
const LABEL_STYLE = { width: "40%" };

type Props = {
  title?: ReactNode;
  tiers: Tier[];
  onEdit?: () => void;
  editPath?: string;
  investment: InvestmentItem;
  extraItems?: DescriptionItem[];
  descriptionsProps?: DescriptionsProps;
};

/**
 * Renders an investment's fields as a description list, with an optional edit action.
 */
const Investment = ({
  title,
  onEdit,
  editPath,
  extraItems = [],
  descriptionsProps = {},
  tiers,
  investment,
}: Props): JSX.Element => {
  const items = renderInvestmentItems(tiers, investment);

  const hasExtra = !!editPath || !!onEdit;

  const extra = hasExtra
    ? <EditButton path={editPath} onClick={onEdit} />
    : undefined;

  return (
    <Descriptions
      bordered
      size="middle"
      title={title}
      extra={extra}
      column={COLUMNS_COUNT}
      labelStyle={LABEL_STYLE}
      {...descriptionsProps}
    >
      {
        [ ...items, ...extraItems ].map(({ label, value }, key) => (
          <Item
            key={key}
            label={label}
          >
            {value}
          </Item>
        ))
      }
    </Descriptions>
  );
};

export default Investment;
